import { getDBEngine, getNodeDBEngines, getSharedDBEngine, LineageNode } from './index';
import { hasPluginIcon } from './Icons';
import { getNodeKind, NodeKind, ROLLUP_GROUP, SEMANTIC_MODEL_GROUP } from './nodeKind';

export interface NodeImageSpec {
    glyph: string;
    source: string;
    sourceType: 'plugin' | 'db';
}

export type SourceBadge = Pick<NodeImageSpec, 'source' | 'sourceType'>;

export const MI_SOURCE_BADGE: SourceBadge = { source: 'metric-insights', sourceType: 'plugin' };
export const GENERIC_DB_BADGE: SourceBadge = { source: 'generic', sourceType: 'db' };
export const GENERIC_BI_BADGE: SourceBadge = { source: 'generic-bi', sourceType: 'plugin' };

const DB_BADGED_KINDS: NodeKind[] = ['table', 'view', 'data-source'];
const BI_KINDS: NodeKind[] = ['semantic-model', 'workbook', 'bi-report'];

const POWERBI_DASHBOARD_REFERENCE_LABEL = 'Microsoft Power BI Dashboard';
const POWERBI_PAGE_REFERENCE_LABEL = 'Microsoft Power BI Page';
const TABLEAU_WORKBOOK_REFERENCE_LABEL = 'Tableau Workbook';
const TABLEAU_PLUGIN_NAME = 'Tableau';

export const getNodeImageSpec = (node: LineageNode): NodeImageSpec | null => {
    const plugin = node.params.pluginName ?? '';
    const biSource = hasPluginIcon(plugin) ? plugin : GENERIC_BI_BADGE.source;

    switch (getNodeKind(node)) {
        case 'table':
        case 'view':
            return { glyph: node.group ?? 'external-metadata', source: getDBEngine(node), sourceType: 'db' };
        case 'data-source':
            return { glyph: 'external-source', source: getDBEngine(node), sourceType: 'db' };
        case 'semantic-model':
            return { glyph: SEMANTIC_MODEL_GROUP, source: biSource, sourceType: 'plugin' };
        case 'workbook':
            return { glyph: ROLLUP_GROUP, source: biSource, sourceType: 'plugin' };
        case 'bi-report': {
            let glyph = 'external-dataset';
            if (node.params.referenceLabel === POWERBI_DASHBOARD_REFERENCE_LABEL) {
                glyph = 'external-dashboard';
            } else if (node.params.referenceLabel === TABLEAU_WORKBOOK_REFERENCE_LABEL) {
                glyph = ROLLUP_GROUP;
            } else if (plugin === TABLEAU_PLUGIN_NAME || node.params.referenceLabel === POWERBI_PAGE_REFERENCE_LABEL) {
                glyph = 'worksheet';
            }

            return { glyph, source: biSource, sourceType: 'plugin' };
        }
        case 'plugin-source':
            return plugin ? { glyph: 'plugin', source: plugin, sourceType: 'plugin' } : null;
        default:
            return null;
    }
};

/**
 * The badge a grouped node wears: the one every member shares; when they differ, the generic database or
 * BI badge for their family. Metric Insights only for its own objects.
 */
export const getCommonSourceBadge = (members: LineageNode[]): SourceBadge => {
    const specs = members.map((member) => getNodeImageSpec(member));

    if (specs.length === 0 || specs.some((spec) => spec === null)) {
        return MI_SOURCE_BADGE;
    }

    const [first] = specs as NodeImageSpec[];
    const shared = specs.every((spec) => spec?.source === first.source && spec?.sourceType === first.sourceType);

    if (shared) {
        return { source: first.source, sourceType: first.sourceType };
    }
    if (specs.every((spec) => spec?.sourceType === 'db')) {
        return GENERIC_DB_BADGE;
    }

    return members.every((member) => BI_KINDS.includes(getNodeKind(member))) ? GENERIC_BI_BADGE : MI_SOURCE_BADGE;
};

/**
 * A backend-grouped node draws its members' glyph and the badge they share, like a cluster the
 * diagram collapses itself: a database only when every member comes from it, the generic database
 * or BI badge when they differ (the backend leaves the shared plugin empty then).
 */
export const getCollapsedImageSpec = (node: LineageNode): NodeImageSpec => {
    const glyph = node.group ?? 'collapsed';
    const spec = getNodeImageSpec(node);
    const kind = getNodeKind(node);

    if (DB_BADGED_KINDS.includes(kind)) {
        return {
            glyph,
            source: getSharedDBEngine(getNodeDBEngines(node)) ?? GENERIC_DB_BADGE.source,
            sourceType: 'db',
        };
    }

    // Grouped reports that share a reference label (Views, Pages) wear that kind's own glyph.
    if (spec && kind === 'bi-report' && node.params.referenceLabel) {
        return spec;
    }
    if (spec && BI_KINDS.includes(kind)) {
        return { ...spec, glyph };
    }

    return spec && node.params.pluginName ? { ...spec, glyph } : { ...MI_SOURCE_BADGE, glyph };
};

/**
 * The kind every member of a diagram-side cluster shares ("Tableau View"), so the cluster reads and
 * draws like the backend's grouped node; empty when the members differ or aren't BI references.
 */
export const getSharedReferenceKind = (members: LineageNode[]): { label: string; glyph: string } | null => {
    const [first] = members;
    const label = first?.params.referenceLabel ?? '';

    if (
        !label ||
        members.some((member) => getNodeKind(member) !== 'bi-report' || member.params.referenceLabel !== label)
    ) {
        return null;
    }

    const glyph = getNodeImageSpec(first)?.glyph;

    return glyph ? { label, glyph } : null;
};
